/**
 * AM Bus Pickup Dialog
 * Staff pick which school's bus just arrived, then confirm who is actually
 * leaving — any School Age child currently checked in at that school is
 * selected by default; staff uncheck anyone staying at the center today.
 * Confirming records the bus arrival and marks the kept children departed
 * for the day (so they stop showing as "Still Here" / no further reminder).
 */
import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { format } from "date-fns";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, Bus } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const AmBusPickupDialog = ({ open, onOpenChange, date, currentUserName, onRecorded }) => {
  const [schoolsList, setSchoolsList] = useState([]);
  const [selectedSchool, setSelectedSchool] = useState("");
  const [candidates, setCandidates] = useState([]);
  const [excludedIds, setExcludedIds] = useState(new Set());
  const [loadingCandidates, setLoadingCandidates] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setSelectedSchool("");
    setCandidates([]);
    setExcludedIds(new Set());
    axios.get(`${API}/schools`)
      .then((res) => setSchoolsList(res.data || []))
      .catch(() => setSchoolsList([]));
  }, [open]);

  const loadCandidates = useCallback((school) => {
    if (!school) { setCandidates([]); return; }
    setLoadingCandidates(true);
    axios.get(`${API}/bus-tracking/am-pickup-candidates/${date}`, { params: { school } })
      .then((res) => {
        setCandidates(res.data.candidates || []);
        setExcludedIds(new Set()); // everyone selected (leaving) by default
      })
      .catch(() => {
        setCandidates([]);
        toast.error("Could not load children for this school");
      })
      .finally(() => setLoadingCandidates(false));
  }, [date]);

  useEffect(() => { loadCandidates(selectedSchool); }, [selectedSchool, loadCandidates]);

  const toggleExcluded = (childId) => {
    setExcludedIds((prev) => {
      const next = new Set(prev);
      if (next.has(childId)) next.delete(childId); else next.add(childId);
      return next;
    });
  };

  const handleConfirm = async () => {
    if (!selectedSchool) {
      toast.error("Please select which school this bus pickup is for");
      return;
    }
    setSubmitting(true);
    try {
      const arrivalTime = format(new Date(), "h:mm a");
      const res = await axios.post(`${API}/bus-tracking`, {
        date,
        busType: "am_pickup",
        arrivalTime,
        recordedBy: currentUserName || "Staff",
        school: selectedSchool,
        excludedChildIds: Array.from(excludedIds),
      });
      const markedCount = res.data.markedChildren?.length || 0;
      toast.success(
        markedCount > 0
          ? `A.M. Bus Picked Up recorded for ${selectedSchool} — ${markedCount} ${markedCount === 1 ? "child" : "children"} marked departed`
          : `A.M. Bus Picked Up recorded for ${selectedSchool}`
      );
      onRecorded?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to record bus arrival");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="am-bus-pickup-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Bus className="w-5 h-5 text-green-700" />
            A.M. Bus Picked Up
          </DialogTitle>
          <DialogDescription>
            Pick the school, then uncheck anyone staying at the center today. Everyone else will be
            marked departed for the day — no further Still Here reminder for them.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <Label className="text-sm text-slate-700">School</Label>
            <Select value={selectedSchool} onValueChange={setSelectedSchool}>
              <SelectTrigger data-testid="am-bus-school-select">
                <SelectValue placeholder="Select school" />
              </SelectTrigger>
              <SelectContent>
                {schoolsList.length === 0 && (
                  <div className="px-2 py-1.5 text-xs text-gray-400">No schools yet — add one via "Manage Schools"</div>
                )}
                {schoolsList.map((s) => (
                  <SelectItem key={s.id} value={s.name} data-testid={`am-bus-school-option-${s.id}`}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {selectedSchool && (
            <div>
              <Label className="text-sm text-slate-700">Children leaving on this bus</Label>
              {loadingCandidates ? (
                <p className="text-sm text-gray-400 py-2 flex items-center gap-2" data-testid="am-bus-candidates-loading">
                  <Loader2 className="w-4 h-4 animate-spin" /> Loading children...
                </p>
              ) : candidates.length === 0 ? (
                <p className="text-sm text-gray-400 py-2" data-testid="am-bus-candidates-empty">
                  No School Age children currently checked in for {selectedSchool} today.
                </p>
              ) : (
                <div className="space-y-1 max-h-56 overflow-y-auto mt-1">
                  {candidates.map((c) => (
                    <label
                      key={c.id}
                      className="flex items-center gap-2 px-3 py-2 rounded-md bg-slate-50 border border-slate-200 cursor-pointer"
                      data-testid={`am-bus-candidate-${c.id}`}
                    >
                      <Checkbox
                        checked={!excludedIds.has(c.id)}
                        onCheckedChange={() => toggleExcluded(c.id)}
                        data-testid={`am-bus-candidate-checkbox-${c.id}`}
                      />
                      <span className="text-sm text-slate-700">{c.name}</span>
                      {excludedIds.has(c.id) && (
                        <span className="text-xs text-amber-600 ml-auto">Staying at center</span>
                      )}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="am-bus-cancel-btn">Cancel</Button>
          <Button
            onClick={handleConfirm}
            disabled={submitting || !selectedSchool}
            className="bg-green-600 hover:bg-green-700 text-white"
            data-testid="am-bus-confirm-btn"
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Confirm Bus Pickup"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
